/**
 * <wishlist-heart> Web Component
 * ──────────────────────────────
 * Renders a heart icon that toggles a product's saved state in the wishlist.
 *
 * Usage (Liquid):
 *   <wishlist-heart data-product-gid="gid://shopify/Product/{{ product.id }}">
 *   </wishlist-heart>
 *
 * States:
 *   - Empty heart (not saved)
 *   - Filled heart (saved)
 *   - Loading (during API call)
 */

class WishlistHeart extends HTMLElement {
  connectedCallback() {
    this.productGid = this.getAttribute('data-product-gid');
    if (!this.productGid) return;

    this.productTitle = this.getAttribute('data-product-title') || '';
    this.render();
    this.button = this.querySelector('.wh-btn');
    this.button.addEventListener('click', this.handleClick.bind(this));

    // Subscribe to wishlist changes
    if (window.slWishlist) {
      this.unsubscribe = window.slWishlist.onChange(() => this.updateState());
      this.updateState();
    } else {
      // Wait for wishlist module to load
      const check = setInterval(() => {
        if (window.slWishlist) {
          clearInterval(check);
          this.unsubscribe = window.slWishlist.onChange(() => this.updateState());
          this.updateState();
        }
      }, 100);
      // Stop checking after 10 seconds
      setTimeout(() => clearInterval(check), 10000);
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  render() {
    this.innerHTML = `
      <button type="button" class="wh-btn" aria-label="Save for Later" title="Save for Later">
        <svg class="wh-icon wh-icon--empty" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
        <svg class="wh-icon wh-icon--filled" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
      </button>
    `;

    // Add label safely via textContent to prevent XSS
    const label = this.getAttribute('data-label');
    if (label) {
      const span = document.createElement('span');
      span.className = 'wh-label';
      span.textContent = label;
      this.querySelector('.wh-btn').appendChild(span);
    }
  }

  updateState() {
    if (!window.slWishlist || !this.productGid) return;
    const saved = window.slWishlist.has(this.productGid);
    this.classList.toggle('wh--saved', saved);

    const btn = this.querySelector('.wh-btn');
    if (btn) {
      const suffix = this.productTitle ? ' ' + this.productTitle : '';
      const label = saved ? 'Remove' + suffix + ' from Saved' : 'Save' + suffix + ' for Later';
      btn.setAttribute('aria-label', label);
      btn.setAttribute('title', label);
    }
  }

  async handleClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if (!window.slWishlist) return;
    if (!this.productGid) return;

    // Prevent double-clicks
    if (this.classList.contains('wh--loading')) return;

    this.classList.add('wh--loading');
    try {
      // Build details from data attributes for guest wishlist storage
      const details = {
        title: this.getAttribute('data-product-title') || '',
        handle: this.getAttribute('data-product-handle') || '',
        image: this.getAttribute('data-product-image') || '',
        price: this.getAttribute('data-product-price') || '',
        compareAtPrice: this.getAttribute('data-product-compare-price') || '',
      };
      await window.slWishlist.toggle(this.productGid, details);
    } finally {
      this.classList.remove('wh--loading');
    }
  }
}

if (!customElements.get('wishlist-heart')) {
  customElements.define('wishlist-heart', WishlistHeart);
}
